import { useEffect, useState } from "react";
import { HasAccess } from "@/components/common/has-access";
import VaulDrawer from "@/components/ui/drawer";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import axiosInstance from "@/api/axios-instance";
import {
  ProfileUpdatePayload,
  UserRequest,
} from "../types/user-request";
import { useApproveUserRequest } from "../hooks/use-approve-user-request";
import { useDeclineUserRequest } from "../hooks/use-decline-user-request";
import { useUpdateReachedOut } from "@/features/users/hooks/use-update-reached-out";
import { useQueryClient } from "@tanstack/react-query";
import { DeclineReasonDialog } from "./decline-reason-dialog";
import {
  PROFESSIONAL_CHANGE_DECLINE_REASONS,
  PROFILE_UPDATE_DECLINE_REASONS,
} from "../utils/decline-reasons";
import ProfessionalChangePicker from "./professional-change-picker";

interface Props {
  request: UserRequest | null;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  onViewUser?: (userId: string) => void;
  // For optimistic local update so the drawer reflects the post-action state
  // even if the row falls out of the current list query's filter.
  onLocalUpdate?: (updated: UserRequest) => void;
}

interface LoadedDocument {
  url: string;
  contentType: string;
}

function payloadOf(req: UserRequest | null): ProfileUpdatePayload {
  return (req?.payload ?? {}) as ProfileUpdatePayload;
}

function filenameFromUrl(url: string, fallback: string): string {
  const last = url.split("/").pop();
  return last && last.length > 0 ? last : fallback;
}

const DocumentPreview = ({
  url,
  contentType,
  filename,
}: {
  url: string;
  contentType: string;
  filename: string;
}) => {
  const isImage = contentType.startsWith("image/");
  const isPdf = contentType === "application/pdf";

  return (
    <div className="border border-gray-200 rounded-md overflow-hidden">
      <div className="flex items-center justify-between px-3 py-2 bg-gray-50 border-b border-gray-200">
        <span
          className="text-xs text-gray-700 truncate"
          title={filename}
        >
          {filename}
        </span>
        <a
          href={url}
          download={filename}
          className="text-xs text-blue-600 hover:underline ml-2 shrink-0"
        >
          Download
        </a>
      </div>
      <div className="bg-gray-100">
        {isImage && (
          <img
            src={url}
            alt={filename}
            className="block w-full max-h-[480px] object-contain bg-white"
          />
        )}
        {isPdf && (
          <iframe
            src={url}
            title={filename}
            className="block w-full h-[600px] bg-white"
          />
        )}
        {!isImage && !isPdf && (
          <div className="p-6 text-center text-sm text-gray-500">
            Preview not available. Use Download.
          </div>
        )}
      </div>
    </div>
  );
};

export const RequestDrawer = ({
  request,
  isOpen,
  onOpenChange,
  onViewUser,
  onLocalUpdate,
}: Props) => {
  const [docs, setDocs] = useState<Array<LoadedDocument | null>>([]);
  const [loading, setLoading] = useState(false);
  const [declineDialogOpen, setDeclineDialogOpen] = useState(false);

  const queryClient = useQueryClient();
  const { mutate: approve, isPending: isApproving } =
    useApproveUserRequest();
  const { mutate: decline, isPending: isDeclining } =
    useDeclineUserRequest();
  const { mutate: toggleReachedOut, isPending: isReachOutPending } =
    useUpdateReachedOut();

  // Fetch each document as a blob whenever the drawer opens on a request.
  // Blob URLs (created/revoked here) let the browser preview the image/PDF
  // inline using the response Content-Type, without exposing the private
  // Spaces URL or stripping the auth header in the request.
  useEffect(() => {
    if (!isOpen || !request || request.document_urls.length === 0) {
      setDocs([]);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setDocs(request.document_urls.map(() => null));

    Promise.all(
      request.document_urls.map(async (_, idx) => {
        try {
          const response = await axiosInstance.get(
            `/user-requests/${request.id}/documents/${idx}/download`,
            { responseType: "blob" },
          );
          const blob = response.data as Blob;
          const contentType =
            (response.headers["content-type"] as string) ||
            blob.type ||
            "application/octet-stream";
          return {
            url: URL.createObjectURL(blob),
            contentType,
          } as LoadedDocument;
        } catch {
          return null;
        }
      }),
    ).then((results) => {
      if (cancelled) {
        results.forEach((r) => r && URL.revokeObjectURL(r.url));
        return;
      }
      setDocs(results);
      setLoading(false);
    });

    return () => {
      cancelled = true;
    };
  }, [isOpen, request?.id]);

  // Clean up blob URLs when the docs array is replaced or the component unmounts.
  useEffect(() => {
    return () => {
      docs.forEach((d) => d && URL.revokeObjectURL(d.url));
    };
  }, [docs]);

  if (!request) {
    return (
      <VaulDrawer
        drawerDirection="right"
        isOpen={isOpen}
        onOpenChange={onOpenChange}
        triggerChildren={null}
        customClass="w-[520px] md:w-[720px] lg:w-[800px] h-full"
        contentChildren={<div className="p-6">No request selected</div>}
      />
    );
  }

  const p = payloadOf(request);
  const hasChanges = !!p.full_name || !!p.gender;
  const acting = isApproving || isDeclining || isReachOutPending;

  // The old → new arrow, mirroring PROFESSIONAL_CHANGE's previous_/new_ pair:
  // once RESOLVED, both sides come from the approval-time snapshot in
  // `resolution` — the live User row has been overwritten (old side would show
  // the new name) and can be renamed again later (payload alone can't prove
  // what was applied). Requests resolved before snapshots shipped fall back to
  // the live value / payload.
  const isResolved = request.status === "RESOLVED";
  const previousNameLabel =
    (isResolved
      ? request.resolution?.previous_full_name ?? request.user.full_name
      : request.user.full_name) || "—";
  const newNameLabel =
    (isResolved ? request.resolution?.new_full_name : null) ?? p.full_name;
  const previousGenderLabel =
    (isResolved
      ? request.resolution?.previous_gender ?? request.user.sex
      : request.user.sex) || "—";
  const newGenderLabel =
    (isResolved ? request.resolution?.new_gender : null) ?? p.gender;

  // PROFESSIONAL_CHANGE has REACHED_OUT as a live (non-terminal) status; for
  // PROFILE_UPDATE only OPEN is live.
  const isActive =
    (request.type === "PROFILE_UPDATE" && request.status === "OPEN") ||
    (request.type === "PROFESSIONAL_CHANGE" &&
      (request.status === "OPEN" || request.status === "REACHED_OUT"));

  const handleToggleReachedOut = () => {
    if (!request.bundle_usage) return;
    const nextValue = request.status !== "REACHED_OUT";
    toggleReachedOut(
      {
        usage_id: request.bundle_usage.id,
        user_id: request.user.id,
        value: nextValue,
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: ["user-requests"] });
        },
      },
    );
  };

  const handleApprove = () => {
    const parts: string[] = [];
    if (p.full_name) parts.push(`Name → "${p.full_name}"`);
    if (p.gender) parts.push(`Gender → "${p.gender}"`);
    if (
      window.confirm(
        `Apply these changes to ${request.user.full_name}?\n\n${parts.join("\n")}`,
      )
    ) {
      approve(request.id, {
        onSuccess: () => onOpenChange(false),
      });
    }
  };

  const handleDeclineSubmit = (reason: string) => {
    decline(
      { id: request.id, reason },
      {
        onSuccess: () => {
          setDeclineDialogOpen(false);
          onOpenChange(false);
        },
      },
    );
  };

  const handleViewUser = () => {
    if (!onViewUser) return;
    onOpenChange(false);
    onViewUser(request.user.id);
  };

  return (
    <>
    <VaulDrawer
      drawerDirection="right"
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      triggerChildren={null}
      customClass="w-[520px] md:w-[720px] lg:w-[800px] h-full"
      contentChildren={
        <div className="h-[calc(100vh-2rem)] w-full overflow-y-auto">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-base font-semibold text-gray-900">
              Profile Update Request
            </h2>
            <p className="text-sm text-gray-600 mt-1">
              {request.user.full_name}
              {request.user.preferred_email
                ? ` · ${request.user.preferred_email}`
                : ""}
            </p>
            <p className="text-xs text-gray-400 mt-1">
              Submitted {new Date(request.created_at).toLocaleString()}
            </p>
          </div>

          <div className="px-6 py-5 space-y-6">
            {request.type === "PROFILE_UPDATE" && (
              <section>
                <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                  Change Details
                </h3>
                {hasChanges ? (
                  <div className="border border-gray-200 rounded-md divide-y divide-gray-200">
                    {p.full_name && (
                      <div className="flex items-baseline gap-3 px-3 py-2">
                        <span className="text-xs text-gray-500 w-16 shrink-0">
                          Name
                        </span>
                        <span className="text-sm text-gray-900">
                          <span className="text-gray-500">
                            {previousNameLabel}
                          </span>
                          <span className="text-gray-400 mx-2">→</span>
                          <span className="font-medium">{newNameLabel}</span>
                        </span>
                      </div>
                    )}
                    {p.gender && (
                      <div className="flex items-baseline gap-3 px-3 py-2">
                        <span className="text-xs text-gray-500 w-16 shrink-0">
                          Gender
                        </span>
                        <span className="text-sm text-gray-900">
                          <span className="text-gray-500">
                            {previousGenderLabel}
                          </span>
                          <span className="text-gray-400 mx-2">→</span>
                          <span className="font-medium">{newGenderLabel}</span>
                        </span>
                      </div>
                    )}
                  </div>
                ) : (
                  <p className="text-sm text-gray-500">No fields proposed.</p>
                )}
              </section>
            )}


            {request.type === "PROFESSIONAL_CHANGE" && (
              <section>
                <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                  Request Details
                </h3>
                <div className="border border-gray-200 rounded-md divide-y divide-gray-200">
                  <div className="flex items-baseline gap-3 px-3 py-2">
                    <span className="text-xs text-gray-500 w-20 shrink-0">
                      Bundle
                    </span>
                    <span className="text-sm text-gray-900">
                      {request.bundle_usage?.bundle.name ?? "—"}
                    </span>
                  </div>
                  <div className="flex items-baseline gap-3 px-3 py-2">
                    <span className="text-xs text-gray-500 w-20 shrink-0">
                      Reason
                    </span>
                    <span className="text-sm text-gray-900">
                      {request.reason || (
                        <span className="text-gray-400">—</span>
                      )}
                    </span>
                  </div>
                  <div className="flex items-baseline gap-3 px-3 py-2">
                    <span className="text-xs text-gray-500 w-20 shrink-0">
                      Usage ID
                    </span>
                    <span className="text-xs font-mono text-gray-500">
                      {request.bundle_usage?.id ?? "—"}
                    </span>
                  </div>
                </div>
              </section>
            )}

            {request.type === "PROFILE_UPDATE" && (
            <section>
              <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                Verification Documents
                <Badge className="ml-2 bg-gray-200 text-gray-700 text-xs">
                  {request.document_urls.length}
                </Badge>
              </h3>
              {request.document_urls.length === 0 ? (
                <p className="text-sm text-gray-500">No documents attached.</p>
              ) : (
                <div className="space-y-3">
                  {request.document_urls.map((url, idx) => {
                    const filename = filenameFromUrl(
                      url,
                      `Document ${idx + 1}`,
                    );
                    const loaded = docs[idx];
                    if (!loaded) {
                      return (
                        <div
                          key={idx}
                          className="border border-gray-200 rounded-md px-3 py-6 text-center text-sm text-gray-500"
                        >
                          {loading
                            ? "Loading preview…"
                            : "Failed to load preview."}
                        </div>
                      );
                    }
                    return (
                      <DocumentPreview
                        key={idx}
                        url={loaded.url}
                        contentType={loaded.contentType}
                        filename={filename}
                      />
                    );
                  })}
                </div>
              )}
            </section>
            )}

            {request.type === "PROFESSIONAL_CHANGE" && isActive && (
              <>
                {(() => {
                  const langs = Array.isArray(request.user.language_preference)
                    ? (request.user.language_preference as string[])
                    : [];
                  const country = request.user.country;
                  if (langs.length === 0 && !country) return null;
                  return (
                    <section>
                      <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                        User Preferences
                      </h3>
                      <div className="border border-gray-200 rounded-md divide-y divide-gray-200">
                        {langs.length > 0 && (
                          <div className="flex items-baseline gap-3 px-3 py-2">
                            <span className="text-xs text-gray-500 w-20 shrink-0">
                              Languages
                            </span>
                            <span className="text-sm text-gray-900">
                              {langs.join(", ")}
                            </span>
                          </div>
                        )}
                        {country && (
                          <div className="flex items-baseline gap-3 px-3 py-2">
                            <span className="text-xs text-gray-500 w-20 shrink-0">
                              Country
                            </span>
                            <span className="text-sm text-gray-900">
                              {country}
                            </span>
                          </div>
                        )}
                      </div>
                    </section>
                  );
                })()}
                <ProfessionalChangePicker
                  request={request}
                  onResolved={(updated) => onLocalUpdate?.(updated)}
                />
              </>
            )}

            {isActive ? (
              <section>
                <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                  Action Items
                </h3>
                <div className="flex flex-wrap items-center gap-2">
                  {request.type === "PROFILE_UPDATE" && (
                    <HasAccess permission="write">
                      <Button
                        className="bg-green-600 hover:bg-green-700 text-white"
                        disabled={acting}
                        onClick={handleApprove}
                      >
                        Approve
                      </Button>
                      <Button
                        variant="outline"
                        className="border-red-300 text-red-600 hover:bg-red-50"
                        disabled={acting}
                        onClick={() => setDeclineDialogOpen(true)}
                      >
                        Decline
                      </Button>
                    </HasAccess>
                  )}
                  {request.type === "PROFESSIONAL_CHANGE" && (
                    <HasAccess permission="write">
                      <Button
                        className={
                          request.status === "REACHED_OUT"
                            ? "bg-green-600 hover:bg-green-700 text-white"
                            : ""
                        }
                        variant={
                          request.status === "REACHED_OUT"
                            ? "default"
                            : "outline"
                        }
                        disabled={acting}
                        onClick={handleToggleReachedOut}
                      >
                        {request.status === "REACHED_OUT"
                          ? "✓ Reached Out"
                          : "Mark as reached out"}
                      </Button>
                      <Button
                        variant="outline"
                        className="border-red-300 text-red-600 hover:bg-red-50"
                        disabled={acting}
                        onClick={() => setDeclineDialogOpen(true)}
                      >
                        Decline
                      </Button>
                    </HasAccess>
                  )}
                  {onViewUser && (
                    <Button
                      variant="outline"
                      onClick={handleViewUser}
                      disabled={acting}
                    >
                      View User
                    </Button>
                  )}
                </div>
              </section>
            ) : (
              <section>
                <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                  Resolution
                </h3>
                <div className="border border-gray-200 rounded-md divide-y divide-gray-200">
                  <div className="flex items-baseline gap-3 px-3 py-2">
                    <span className="text-xs text-gray-500 w-24 shrink-0">
                      Status
                    </span>
                    <span className="text-sm font-medium text-gray-900">
                      {request.status}
                    </span>
                  </div>
                  {request.resolved_at && (
                    <div className="flex items-baseline gap-3 px-3 py-2">
                      <span className="text-xs text-gray-500 w-24 shrink-0">
                        Resolved by
                      </span>
                      <span className="text-sm text-gray-900">
                        {request.resolved_by_name ??
                          request.resolved_by ??
                          "unknown"}
                        <span className="text-gray-500 ml-2">
                          on{" "}
                          {new Date(request.resolved_at).toLocaleString()}
                        </span>
                      </span>
                    </div>
                  )}
                  {request.status === "DECLINED" &&
                    request.resolution?.decline_reason && (
                      <div className="flex items-baseline gap-3 px-3 py-2">
                        <span className="text-xs text-gray-500 w-24 shrink-0">
                          Decline reason
                        </span>
                        <span className="text-sm text-red-700">
                          {request.resolution.decline_reason}
                        </span>
                      </div>
                    )}
                  {request.type === "PROFESSIONAL_CHANGE" &&
                    request.status === "RESOLVED" &&
                    (request.resolution?.previous_professional_name ||
                      request.resolution?.new_professional_name) && (
                      <div className="flex items-start gap-3 px-3 py-2">
                        <span className="text-xs text-gray-500 w-24 shrink-0 pt-1">
                          Change
                        </span>
                        <div className="flex flex-col gap-1 text-sm">
                          <span>
                            <span className="text-gray-500">From:</span>{" "}
                            <span className="text-gray-900">
                              {request.resolution.previous_professional_name ??
                                "Not assigned"}
                            </span>
                          </span>
                          <span>
                            <span className="text-gray-500">To:</span>{" "}
                            <span className="font-medium text-gray-900">
                              {request.resolution.new_professional_name ??
                                "Not assigned"}
                            </span>
                          </span>
                        </div>
                      </div>
                    )}
                </div>
                {onViewUser && (
                  <div className="mt-3">
                    <Button variant="outline" onClick={handleViewUser}>
                      View User
                    </Button>
                  </div>
                )}
              </section>
            )}
          </div>
        </div>
      }
    />
    <DeclineReasonDialog
      isOpen={declineDialogOpen}
      onOpenChange={setDeclineDialogOpen}
      userName={request.user.full_name}
      isSubmitting={isDeclining}
      onSubmit={handleDeclineSubmit}
      reasons={
        request.type === "PROFESSIONAL_CHANGE"
          ? PROFESSIONAL_CHANGE_DECLINE_REASONS
          : PROFILE_UPDATE_DECLINE_REASONS
      }
      title={
        request.type === "PROFESSIONAL_CHANGE"
          ? `Decline professional change for ${request.user.full_name}`
          : undefined
      }
    />
    </>
  );
};

export default RequestDrawer;
